/**
 * ④案件の変更一覧と案件の差分（F4）をルートへ繋ぐ受け口。
 *
 * **④は今の案件の変更一覧（`PJ_DIFF_ROUTE`）の中身そのものである。** `/pjs/$pjNumber` の
 * URL は変わらないまま、中身が要素表（`spec_graph_*`）を読む。欄ごとの差分は別のルート
 * （`PJ_FIELD_DIFF_ROUTE`）が受ける。案件を選ぶのは案件の一覧（`/pjs`）の役目で、
 * この受け口は選ばれた案件をパスから読むだけである。
 *
 * **読取が 2 本揃うまで描かない。** 空の配列を「案件の変更 0 件」として渡すと、読取が
 * 途中であることと案件が何も触っていないことが見分けられなくなる（`PjLanesPage` の
 * docblock）。読み込み中は骨組み、失敗は失敗として出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の変更一覧と差分のビューを、URL から埋めた入力とともにルートへ繋ぐ。
 * @graph-connects PjLanesPage [calls] PJ の変更一覧の描画
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 * @graph-connects readPjNumberParam [calls] パスの綴りを案件番号へ読む
 * @graph-connects PjDiffPage [calls] PJ の差分の描画
 * @graph-connects usePjDiff [calls] 案件の差分の取得
 */
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState, NotFoundState } from '../../components/spec/fallback-states.js';
import {
  usePjChanges,
  usePjDiff,
  usePjRequirements,
} from '../../hooks/queries/spec-graph-element-pj-queries.js';
import { PjDiffPage } from '../PjDiffPage.js';
import { PjLanesPage } from '../PjLanesPage.js';
import { PJS_ROUTE, PJ_DIFF_ROUTE, PJ_FIELD_DIFF_ROUTE } from '../route-paths.js';
import { readPjNumberParam } from './pj-number.js';

/**
 * ④PJ の変更一覧のルートの中身。案件番号を URL から読み、2 本の読取を揃えて描く。
 *
 * **案件の切り替えはこの画面が持たない。** 案件はこの画面の主語でパスに載っているので、
 * 画面の中に選択の状態をもう 1 つ持つと、URL と画面のどちらが正かが決まらなくなる。別の
 * 案件へ移るのは案件の一覧（`/pjs`）からである。
 *
 * @returns PJ の変更一覧の画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の変更一覧のルートへ、案件の変更の行・要求を揃えて描く。
 * @graph-connects PjLanesPage [calls] PJ の変更一覧の描画
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 */
export function PjLanesRoutePage() {
  const { t } = useTranslation();
  const { pjNumber: raw } = useParams({ from: PJ_DIFF_ROUTE });
  const pjNumber = readPjNumberParam(raw);

  /* 読めない綴りのときは読取を 1 本も投げない。**関門を通らない番号で問い合わせてから失敗を
     描くと、URL の打ち間違いが「取得に失敗しました」として出る。** 取得する側を別の部品に
     分けてあるのは、フックの数と順序を描画のたびに同じに保つためである。 */
  if (pjNumber === null) {
    return (
      <NotFoundState
        id={raw}
        action={
          <Link to={PJS_ROUTE} className="text-sm text-[var(--color-accent-primary)] underline">
            {t('spec.backToList')}
          </Link>
        }
      />
    );
  }

  return <PjLanesRouteBody pjNumber={pjNumber} />;
}

/** `PjLanesRouteBody` の props。 @graph-connects none */
interface PjLanesRouteBodyProps {
  /** URL から読んだ案件番号（1 以上の整数） */
  pjNumber: number;
}

/**
 * ④PJ の変更一覧の中身。2 本の読取を揃えてからビューへ渡す。
 *
 * **2 本が揃うまで描かない。** 変更の行・要求のどちらか 1 本でも空のまま渡すと、
 * 「まだ来ていない」が「案件が何も触っていない」として描かれる（`PjLanesPage` の docblock）。
 *
 * @param props 案件番号
 * @returns 読み込み中 / 失敗 / PJ の変更一覧
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行・要求の 2 本を揃え、読み込み中・失敗・本体の 3 状態で出し分ける。
 * @graph-connects PjLanesPage [calls] PJ の変更一覧の描画
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 */
function PjLanesRouteBody({ pjNumber }: PjLanesRouteBodyProps) {
  const { t } = useTranslation();
  const changes = usePjChanges(pjNumber);
  const requirements = usePjRequirements(pjNumber);

  const error = changes.error ?? requirements.error;
  if (error !== null && error !== undefined) return <ErrorState error={error} />;

  if (changes.data === undefined || requirements.data === undefined) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="720px" />
      </div>
    );
  }

  return (
    <PjLanesPage
      pjNumber={pjNumber}
      pjChanges={changes.data.changes}
      requirements={requirements.data.requirements}
    />
  );
}

/**
 * PJ の差分のルートの中身。案件番号を URL から読み、差分の読取を揃えて描く。
 *
 * 読めない綴りのときは読取を投げずに未存在を出す（変更一覧と同じ関門）。
 *
 * @returns PJ の差分の画面
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の差分のルートへ、URL から読んだ案件番号で差分を揃えて描く。
 * @graph-connects PjDiffPage [calls] PJ の差分の描画
 * @graph-connects readPjNumberParam [calls] パスの綴りを案件番号へ読む
 */
export function PjDiffRoutePage() {
  const { t } = useTranslation();
  const { pjNumber: raw } = useParams({ from: PJ_FIELD_DIFF_ROUTE });
  const pjNumber = readPjNumberParam(raw);

  if (pjNumber === null) {
    return (
      <NotFoundState
        id={raw}
        action={
          <Link to={PJS_ROUTE} className="text-sm text-[var(--color-accent-primary)] underline">
            {t('spec.backToList')}
          </Link>
        }
      />
    );
  }

  return <PjDiffRouteBody pjNumber={pjNumber} />;
}

/**
 * PJ の差分の中身。差分の読取が揃うまで骨組みを出す。
 *
 * @param props 案件番号
 * @returns 読み込み中 / 失敗 / PJ の差分
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の差分の読取を、読み込み中・失敗・本体の 3 状態で出し分ける。
 * @graph-connects usePjDiff [calls] 案件の差分の取得
 */
function PjDiffRouteBody({ pjNumber }: PjLanesRouteBodyProps) {
  const { t } = useTranslation();
  const diff = usePjDiff(pjNumber);

  if (diff.error !== null && diff.error !== undefined) return <ErrorState error={diff.error} />;

  if (diff.data === undefined) {
    return (
      <div aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="720px" />
      </div>
    );
  }

  return <PjDiffPage pjNumber={pjNumber} diff={diff.data} />;
}
